// @ts-check
import { useEffect, useRef, useState } from "react"

/**
 * @typedef {{ sessionId: string | null, count: number, messageCount: number }} ArtifactSnapshot
 */

/**
 * Whether the chat has an artifact that the user has not seen.
 *
 * Opening an older chat is not news, but a first review is: its document can
 * exist before the chat has a saved message.
 * @param {ArtifactSnapshot & { isNew: boolean }} seen
 * @param {ArtifactSnapshot} next
 */
export function nextArtifactState(seen, next) {
  if (seen.sessionId !== next.sessionId) {
    return { ...next, isNew: next.count > 0 && next.messageCount === 0 }
  }
  return { ...next, isNew: seen.isNew || next.count > seen.count }
}

/**
 * @param {ArtifactSnapshot & { isReady: boolean, isOpen: boolean }} props
 * @returns {boolean} True from a new artifact until the user opens the list.
 */
export function useNewArtifact({ sessionId, count, messageCount, isReady, isOpen }) {
  const seenRef = useRef({ sessionId: /** @type {string | null} */ (null), count: 0, messageCount: 0, isNew: false })
  const [isNew, setIsNew] = useState(false)

  useEffect(() => {
    if (!isReady) return

    const next = nextArtifactState(seenRef.current, { sessionId, count, messageCount })
    seenRef.current = { ...next, isNew: next.isNew && !isOpen }
    setIsNew(seenRef.current.isNew)
  }, [sessionId, count, messageCount, isReady, isOpen])

  return isNew
}
